import { Reducer } from "redux";
import { CALL_API } from "../helpers/middleware/api";
import createRequestTypes from "../helpers/createRequestTypes";
import { MarketingConsent } from "types";
import { addYears } from "date-fns";

const FETCH_HOOPLA_MARKETING_CONSENTS = createRequestTypes("FETCH_HOOPLA_MARKETING_CONSENTS");
export const fetchHooplaMarketingConsents = (userId: number) => ({
  [CALL_API]: {
    types: FETCH_HOOPLA_MARKETING_CONSENTS,
    endpoint: `/api/v2.0/users/${userId}/marketing_consents/hoopla/`,
  },
});

const UPDATE_HOOPLA_MARKETING_CONSENTS = createRequestTypes("UPDATE_HOOPLA_MARKETING_CONSENTS");
export const updateHooplaMarketingConsent = (value: boolean, userId: number) => {
  if (value) {
    return {
      [CALL_API]: {
        types: UPDATE_HOOPLA_MARKETING_CONSENTS,
        endpoint: `/api/v2.0/users/${userId}/marketing_consents/_hoopla_consent`,
        options: { method: "POST" },
        noJson: true,
      },
    };
  } else {
    return {
      [CALL_API]: {
        types: UPDATE_HOOPLA_MARKETING_CONSENTS,
        endpoint: `/api/v2.0/users/${userId}/marketing_consents/_hoopla_revoke`,
        options: {
          method: "POST",
        },
        noJson: true,
      },
    };
  }
};

const FETCH_ORGANIZATION_MARKETING_CONSENTS = createRequestTypes(
  "FETCH_ORGANIZATION_MARKETING_CONSENTS",
);
export const fetchOrganizationMarketingConsents = (userId: number) => ({
  [CALL_API]: {
    types: FETCH_ORGANIZATION_MARKETING_CONSENTS,
    endpoint: `/api/v2.0/users/${userId}/marketing_consents/organizations/`,
  },
});

const UPDATE_ORGANIZATION_MARKETING_CONSENTS = createRequestTypes(
  "UPDATE_ORGANIZATION_MARKETING_CONSENTS",
);
export const updateOrganizationMarketingConsent = (
  value: boolean,
  userId: number,
  organizationId: number,
) => {
  if (value) {
    return {
      [CALL_API]: {
        types: UPDATE_ORGANIZATION_MARKETING_CONSENTS,
        endpoint: `/api/v2.0/users/${userId}/marketing_consents/_organization_consent`,
        options: { method: "POST", body: JSON.stringify({ organization_id: organizationId }) },
        additional: { organizationId },
        noJson: true,
      },
    };
  } else {
    return {
      [CALL_API]: {
        types: UPDATE_ORGANIZATION_MARKETING_CONSENTS,
        endpoint: `/api/v2.0/users/${userId}/marketing_consents/_organization_revoke`,
        options: { method: "POST", body: JSON.stringify({ organization_id: organizationId }) },
        additional: { organizationId },
        noJson: true,
      },
    };
  }
};

export interface MarketingConsentsState {
  fetchingOrg: boolean;
  fetchedOrg: boolean;
  fetchingHoopla: boolean;
  fetchedHoopla: boolean;
  patchingHoopla: boolean;
  patchingOrg: Set<number>;
  hooplaConsent: MarketingConsent | null;
  orgConsents: MarketingConsent[];
  error: boolean;
}

const initialState: MarketingConsentsState = {
  fetchingOrg: false,
  fetchedOrg: false,
  fetchingHoopla: false,
  fetchedHoopla: false,
  patchingHoopla: false,
  patchingOrg: new Set(),
  hooplaConsent: null,
  orgConsents: [],
  error: false,
};

const reducer: Reducer<MarketingConsentsState> = (
  state = initialState,
  action,
): MarketingConsentsState => {
  switch (action.type) {
    case FETCH_HOOPLA_MARKETING_CONSENTS.REQUEST:
      return { ...state, fetchingHoopla: true, fetchedHoopla: false };
    case FETCH_ORGANIZATION_MARKETING_CONSENTS.REQUEST:
      return { ...state, fetchingOrg: true, fetchedOrg: false };
    case FETCH_HOOPLA_MARKETING_CONSENTS.SUCCESS: {
      return {
        ...state,
        fetchingHoopla: false,
        fetchedHoopla: true,
        hooplaConsent: action.response,
      };
    }
    case FETCH_ORGANIZATION_MARKETING_CONSENTS.SUCCESS: {
      return { ...state, fetchingOrg: false, fetchedOrg: true, orgConsents: action.response };
    }
    case FETCH_HOOPLA_MARKETING_CONSENTS.FAILURE:
      return { ...state, fetchingHoopla: false, fetchedHoopla: true, error: true };
    case FETCH_ORGANIZATION_MARKETING_CONSENTS.FAILURE:
      return { ...state, fetchingOrg: false, fetchedOrg: true, error: true };

    case UPDATE_ORGANIZATION_MARKETING_CONSENTS.REQUEST: {
      return { ...state, patchingOrg: new Set(state.patchingOrg).add(action.organizationId) };
    }
    case UPDATE_ORGANIZATION_MARKETING_CONSENTS.SUCCESS: {
      const patchingOrg = new Set(state.patchingOrg);
      const date = new Date();
      patchingOrg.delete(action.organizationId);
      const consent = state.orgConsents.find((c) => c.organization_id === action.organizationId);
      const newConsent: MarketingConsent = {
        ...consent,
        is_active: !consent.is_active,
        consent_date: date.toUTCString(),
        expiry_date: addYears(date, 2).toUTCString(),
      };
      const newConsents = [
        ...state.orgConsents.filter((c) => c.organization_id !== action.organizationId),
        newConsent,
      ].sort((a, b) => {
        if (a.organization_name.localeCompare(b.organization_name) === 0) {
          return a.organization_id - b.organization_id;
        } else {
          return a.organization_name.localeCompare(b.organization_name);
        }
      });
      return { ...state, patchingOrg, orgConsents: newConsents };
    }

    case UPDATE_HOOPLA_MARKETING_CONSENTS.REQUEST: {
      return { ...state, patchingHoopla: true };
    }
    case UPDATE_HOOPLA_MARKETING_CONSENTS.SUCCESS: {
      const date = new Date();
      return {
        ...state,
        patchingHoopla: false,
        hooplaConsent: {
          ...state.hooplaConsent,
          is_active: !state.hooplaConsent.is_active,
          consent_date: date.toUTCString(),
          expiry_date: addYears(date, 2).toUTCString(),
        },
      };
    }

    default:
      return state;
  }
};

export default reducer;
